import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hatBezug } from "@/office/excel/formeln";
import { filledCells, formulaCells, isError, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

export const lektionErsteFormel: Lesson = {
  id: "erste-formel",
  title: "Die erste Formel",
  summary: "Alles beginnt mit einem Gleichheitszeichen – und mit der Entscheidung, auf Zellen zu zeigen statt zu tippen.",
  durationMinutes: 10,
  learningGoals: [
    "Eine Formel aus Bezügen statt aus getippten Zahlen aufbauen",
    "Die Rechenreihenfolge von Excel kennen und mit Klammern steuern",
    "Fehlerwerte lesen und beheben",
  ],
  steps: [
    {
      id: "gleichheitszeichen",
      title: "Das Gleichheitszeichen schaltet um",
      explanation: [
        "Ein Gleichheitszeichen am Anfang der Eingabe schaltet Excel vom Notieren aufs " +
          "Rechnen um. Alles danach wird als Anweisung gelesen: =4+3 ergibt 7, =B2*C2 " +
          "multipliziert den Inhalt zweier Zellen. Ohne Gleichheitszeichen bleibt " +
          "„4+3“ schlicht Text.",
        "Die Rechenzeichen sind die üblichen, mit zwei Besonderheiten: Der Stern " +
          "multipliziert, der Schrägstrich teilt, und das Dach ^ potenziert. Die " +
          "Reihenfolge folgt der Schulmathematik – Punkt vor Strich, Klammern zuerst. " +
          "=2+3*4 ergibt 14, nicht 20.",
        "Der entscheidende Handgriff ist aber ein anderer: Statt eine Adresse zu " +
          "tippen, klickst du die Zelle an, während die Formel offen ist. Excel setzt " +
          "den Bezug selbst ein. Das ist nicht nur schneller, es ist auch fehlerfrei – " +
          "vertippte Bezüge sind eine der häufigsten Fehlerquellen überhaupt, weil sie " +
          "meist auf eine existierende Nachbarzelle zeigen und deshalb ein plausibles " +
          "falsches Ergebnis liefern.",
      ],
      keyPoints: [
        "Jede Formel beginnt mit =. Ohne das bleibt die Eingabe Text.",
        "Punkt vor Strich, Klammern zuerst – wie in der Schule.",
        "Zellen anklicken statt Adressen tippen. F2 öffnet eine Zelle zum Bearbeiten.",
      ],
      task: {
        instruction:
          "Schreibe in eine freie Zelle deine erste Formel, die mit zwei Zellen deiner " +
          "Liste rechnet – zum Beispiel Menge mal Preis.",
        hint: "= tippen, die erste Zelle anklicken, * tippen, die zweite Zelle anklicken, Eingabetaste.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Klicke deine Formelzelle an und sieh in der Bearbeitungsleiste nach, ob dort Zellbezüge stehen.",
              true,
            );
          }

          if (filledCells(mappe).length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          const formeln = formulaCells(mappe);
          if (formeln.length === 0) {
            return fail(
              "Es steht noch keine Formel in der Arbeitsmappe.",
              "Tippe in eine freie Zelle ein Gleichheitszeichen, klicke dann eine Zelle deiner Liste an.",
            );
          }

          const mitBezug = formeln.filter((zelle) => hatBezug(zelle.formula));
          if (mitBezug.length === 0) {
            const erste = formeln[0];
            return fail(
              `In ${erste.address} steht zwar eine Formel („${erste.formula}“), sie rechnet aber ` +
                "nur mit getippten Zahlen und nicht mit Zellen.",
              "Klicke die Zellen an, statt die Zahlen abzutippen – dann ändert sich das Ergebnis mit, " +
                "wenn sich die Werte ändern.",
            );
          }

          const erste = mitBezug[0];
          return pass(
            `In ${erste.address} rechnet „${erste.formula}“ mit Zellbezügen und liefert „${erste.text}“.`,
          );
        },
      },
    },
    {
      id: "fehlerwerte",
      title: "Fehlerwerte sind Hinweise, keine Katastrophen",
      explanation: [
        "Meldet Excel einen Fehler, sagt es meist ziemlich genau, was los ist – man " +
          "muss die Kürzel nur lesen können. #WERT! heißt: In der Rechnung steckt " +
          "Text, wo eine Zahl hingehört – oft eine Zelle mit mitgetippter Einheit. " +
          "#DIV/0! heißt: Es wurde durch eine leere oder null-wertige Zelle geteilt.",
        "#BEZUG! ist der ernsteste der Fehler: Eine Zelle, auf die die Formel verwies, " +
          "wurde gelöscht. Anders als die übrigen lässt er sich nicht durch Korrigieren " +
          "der Eingabe beheben – der Bezug ist wirklich weg und muss neu gesetzt " +
          "werden. Deshalb ist Zeilenlöschen in fremden Tabellen eine riskante Übung.",
        "#NAME? bedeutet, dass Excel ein Wort in der Formel nicht kennt: ein " +
          "vertippter Funktionsname oder ein benannter Bereich, den es nicht gibt. " +
          "Und ##### ist überhaupt kein Fehler, sondern nur eine zu schmale Spalte – " +
          "ein Doppelklick auf den Spaltentrenner behebt es.",
      ],
      keyPoints: [
        "#WERT! = Text in der Rechnung, #DIV/0! = Division durch null.",
        "#BEZUG! = die Zelle wurde gelöscht; der Bezug muss neu gesetzt werden.",
        "##### ist kein Fehler, nur eine zu schmale Spalte.",
      ],
      task: {
        instruction: "Sorge dafür, dass in deiner Arbeitsmappe keine Fehlerwerte stehen.",
        hint: "Klicke die Fehlerzelle an. Über das Warndreieck bietet Excel „Fehler nachverfolgen“ an und zeigt, worauf die Formel verweist.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der Werttyp einer Zelle",
              "1.4",
              "Sieh dein Blatt durch: Steht irgendwo eine Raute mit einem Wort dahinter?",
            );
          }

          const zellen = filledCells(mappe);
          if (zellen.length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          const fehler = zellen.filter(isError);
          return fromCriteria(
            [
              criterion(
                `Mindestens eine Formel in der Arbeitsmappe (gefunden: ${formulaCells(mappe).length})`,
                formulaCells(mappe).length > 0,
              ),
              criterion(
                fehler.length === 0
                  ? "Keine Zelle zeigt einen Fehlerwert"
                  : `Fehlerwerte in ${fehler.map((zelle) => `${zelle.address} (${zelle.text})`).slice(0, 3).join(", ")}`,
                fehler.length === 0,
              ),
            ],
            {
              success: "Alle Formeln rechnen fehlerfrei.",
              failure: "Da steht noch mindestens ein Fehlerwert.",
              hint: "Bei #WERT! prüfe, ob eine der beteiligten Zellen Text enthält – etwa eine Zahl mit angehängter Einheit.",
            },
          );
        },
      },
    },
  ],
};
